/**
 * TodaySchedule — what is actually on today, on the dashboard.
 *
 * CAL-PRO-4. Rennan: *"add importnat things to your today page and
 * dashboard page."*
 *
 * The dashboard has never read `calendar_events` at all. Its one
 * calendar-shaped number, "Today's shoots", is counted off projects'
 * `scheduled_date` — so a studio could have three blocks and a client
 * booking today and the page would say nothing about any of them. This
 * is the missing half: the day itself, from the same stream the
 * calendar renders, so the two cannot disagree.
 *
 * Deliberately a LIST and not a grid. A dashboard answers "what now",
 * and an hour grid at this width would be four pixels an event with
 * nowhere to put a name. Times down the left, the next thing marked,
 * and the calendar one tap away for everything else.
 *
 * When today is empty it shows the next thing instead of "nothing on".
 * An empty state that tells you nothing is a waste of the space it
 * takes; "Nothing today — next is Thursday" is the answer to the
 * question somebody with an empty day actually has.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useCalendarStream, type CalendarStreamRow } from '@/services/calendarStream';
import { touchesDay } from '@/components/calendar/timeGrid';
import { cn } from '@/lib/utils';

/** Same palette as the calendar grid and iOS, so the three agree. */
const TYPE_ACCENT: Record<CalendarStreamRow['type'], string> = {
  due_date: '#DB4433',
  milestone: '#E8C547',
  personal: '#2D6E70',
  booking: '#3B82F6',
};

/** Long enough to be worth a card, short enough not to be a page. */
const MAX_ROWS = 6;

function Row({ row, next }: { row: CalendarStreamRow; next: boolean }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden
        className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
        style={{ background: TYPE_ACCENT[row.type] }}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'shrink-0 text-[13px] tabular-nums',
              next ? 'font-semibold text-foreground dark:text-white' : 'g-mute',
            )}
          >
            {row.allDay ? 'All day' : format(row.date, 'h:mm a')}
          </span>
          {next && (
            <span className="shrink-0 rounded-full bg-foreground/[0.07] px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground/70 dark:bg-white/10 dark:text-white/70">
              Next
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-[15px] leading-snug text-foreground dark:text-white">
          {row.title}
        </span>
        {row.subtitle && row.subtitle !== row.title && (
          <span className="g-mute block truncate text-[13px]">{row.subtitle}</span>
        )}
      </span>
    </li>
  );
}

export function TodaySchedule() {
  const { rows } = useCalendarStream();

  const { today, upNext, nextId } = useMemo(() => {
    const now = new Date();
    const sorted = [...rows].sort((a, b) => a.date.getTime() - b.date.getTime());
    // `touchesDay` and not `isSameDay(r.date, now)`: the calendar's
    // own test for whether something belongs to a day, so the two
    // cannot disagree about what "today" contains — which is the whole
    // reason the stream is shared. It catches the two cases a start-
    // date comparison misses: a booking that began last night and is
    // still running, and a due date stamped at midnight.
    const mine = sorted
      .filter((r) => touchesDay({ id: r.id, start: r.date, end: r.endAt, allDay: r.allDay }, now))
      // All-day things first: they are the frame the rest of the day
      // sits in, not an event at midnight.
      .sort((a, b) => Number(!!b.allDay) - Number(!!a.allDay) || a.date.getTime() - b.date.getTime());

    // The next thing still to come — "still running" counts, because
    // the thing you are in the middle of is the thing you want marked.
    const ahead = mine.find((r) => !r.allDay && r.endAt.getTime() >= now.getTime());

    return {
      today: mine,
      // Only looked at when today is empty, so the cost of the scan is
      // paid on the days with nothing on them.
      upNext: mine.length ? null : (sorted.find((r) => r.endAt.getTime() >= now.getTime()) ?? null),
      nextId: ahead?.id ?? null,
    };
  }, [rows]);

  const shown = today.slice(0, MAX_ROWS);
  const more = today.length - shown.length;

  return (
    <div className="g-card p-7">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="g-h3">Today</h3>
        <Link to="/calendar" className="g-mute text-[13px] no-underline hover:underline">
          {format(new Date(), 'EEE d MMM')}
        </Link>
      </div>

      {shown.length > 0 ? (
        <>
          <ul className="mt-5 space-y-4">
            {shown.map((row) => (
              <Row key={row.id} row={row} next={row.id === nextId} />
            ))}
          </ul>
          {more > 0 && (
            <Link to="/calendar" className="g-mute mt-4 inline-block text-[13px] no-underline hover:underline">
              {more} more {more === 1 ? 'thing' : 'things'} today →
            </Link>
          )}
        </>
      ) : (
        <div className="mt-4">
          <p className="text-[16px] leading-relaxed">
            {upNext
              // `upNext` is only looked for when today is empty, so
              // it is never today: the day name is always the answer.
              ? `Nothing today. Next is ${format(upNext.date, 'EEEE')}.`
              : 'Nothing on the calendar today.'}
          </p>
          {upNext && (
            <ul className="mt-4">
              <Row row={upNext} next={false} />
            </ul>
          )}
          <Link to="/calendar" className="g-pill mt-4 inline-flex no-underline">
            Open the calendar
          </Link>
        </div>
      )}
    </div>
  );
}

export default TodaySchedule;
